import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useUserRolePermissions } from 'hooks';
import { format } from 'date-fns';
import StatusChip from '../components/StatusChip';

const LoadRow = ({
  driver,
  load,
  title,
  subtitle,
  onView,
  onDuplicate,
  onEdit,
  onDelete,
  onFavorite,
  onUnfavorite,
}) => {
  const { isDispatchingManage } = useUserRolePermissions();
  const [menuAnchor, setMenuAnchor] = useState();

  const isCompleted = R.equals(load.status, 'Completed');
  const { isFavorite } = load;

  // prettier-ignore
  const handleMenuOpen = R.compose(
    setMenuAnchor, 
    R.prop('currentTarget')
  );

  const handleView = () => {
    onView(load.loadId);
    setMenuAnchor();
  };

  const handleDuplicate = () => {
    onDuplicate(load.loadId);
    setMenuAnchor();
  };

  const handleEdit = () => {
    onEdit(load.loadId);
    setMenuAnchor();
  };

  const handleDelete = () => {
    onDelete({ driver, load });
    setMenuAnchor();
  };

  const handleFavorite = () => {
    onFavorite({ loadId: load.loadId });
    setMenuAnchor();
  };

  const handleUnfavorite = () => {
    onUnfavorite({ loadId: load.loadId });
    setMenuAnchor();
  };

  // prettier-ignore
  const favoriteIcon = load.isFavorite 
    ? <StarIcon color="secondary" />
    : <StarBorderIcon color="secondary" />;

  const cellStyles = { borderBottom: 0, px: 1 };

  const formatDate = date => format(new Date(date), 'MM/dd/yyyy');

  return (
    <>
      <TableRow key={load.loadId} sx={{ backgroundColor: 'white', boxShadow: 1, height: 76 }}>
        <TableCell sx={cellStyles}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography noWrap variant="subtitle1">
              {title}
            </Typography>
            <IconButton
              disabled={
                !isDispatchingManage ||
                ['draft', 'pending', 'warning'].includes(load.status?.toLowerCase())
              }
              onClick={load.isFavorite ? handleUnfavorite : handleFavorite}
              style={{ pointerEvents: isDispatchingManage ? 'default' : 'none' }}
            >
              {favoriteIcon}
            </IconButton>
          </Stack>
        </TableCell>
        <TableCell sx={cellStyles}>{subtitle}</TableCell>
        <TableCell sx={cellStyles}>{load.poNumber}</TableCell>
        <TableCell sx={cellStyles}>
          <StatusChip status={load.status} />
        </TableCell>
        <TableCell sx={cellStyles}>{formatDate(load.createdDate)}</TableCell>
        <TableCell sx={cellStyles}>{formatDate(load.updatedDate)}</TableCell>
        <TableCell sx={{ borderBottom: 0, px: 0 }}>
          <IconButton onClick={handleMenuOpen}>
            <MoreVertIcon />
          </IconButton>
        </TableCell>
      </TableRow>

      {menuAnchor && (
        <Menu anchorEl={menuAnchor} onClose={() => setMenuAnchor()} open>
          <MenuItem onClick={handleView}>View</MenuItem>
          {isDispatchingManage && isCompleted && (
            <MenuItem onClick={handleDuplicate}>Duplicate</MenuItem>
          )}
          {isDispatchingManage && !isCompleted && <MenuItem onClick={handleEdit}>Edit</MenuItem>}
          {isDispatchingManage && !isCompleted && (
            <MenuItem onClick={handleDelete}>Delete</MenuItem>
          )}
          {isDispatchingManage && !isFavorite && (
            <MenuItem
              disabled={
                !isDispatchingManage ||
                ['draft', 'pending', 'warning'].includes(load.status?.toLowerCase())
              }
              onClick={handleFavorite}
            >
              Add to Favorites
            </MenuItem>
          )}
          {isDispatchingManage && isFavorite && (
            <MenuItem onClick={handleUnfavorite}>Remove from Favorites</MenuItem>
          )}
        </Menu>
      )}
    </>
  );
};

LoadRow.propTypes = {
  driver: PropTypes.shape({}),
  load: PropTypes.shape({
    createdDate: PropTypes.string,
    isFavorite: PropTypes.bool,
    loadId: PropTypes.string,
    poNumber: PropTypes.string,
    status: PropTypes.oneOf(['DRAFT', 'PENDING', 'SCHEDULED', 'COMPLETED', 'WARNING']),
    updatedDate: PropTypes.string,
  }).isRequired,
  onDelete: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onFavorite: PropTypes.func.isRequired,
  onUnfavorite: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  subtitle: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};
LoadRow.defaultProps = {
  driver: undefined,
};

export default LoadRow;
